import { useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { SelectedMeeting } from "../../../pages/notes/meetingNotes";
import { Meeting, MeetingStatus } from "../../../models/meeting";

const getTimeInputSx = () => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#FFFFFF",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#242C63" },
    "&.Mui-focused fieldset": {
      borderColor: "#242C63",
      boxShadow: "0 0 0 3px rgba(227,216,191,0.5)",
    },
  },
});

interface CreateMeetingCardProps {
  onCreateMeeting: (meeting: SelectedMeeting) => void;
  onCancel: () => void;
  nextId: string;
}

const CreateMeetingCard = ({
  onCreateMeeting,
  onCancel,
  nextId,
}: CreateMeetingCardProps) => {
  const { t, i18n } = useTranslation();
  const [meetingName, setMeetingName] = useState("");
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const useAmPm = i18n.language?.toLowerCase().startsWith("en") ?? false;

  const [startTime, setStartTime] = useState<Dayjs | null>(
    dayjs().hour(9).minute(0).second(0).millisecond(0),
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(
    dayjs().hour(10).minute(0).second(0).millisecond(0),
  );

  const [invitees, setInvitees] = useState<string[]>([]);
  const [participantInput, setParticipantInput] = useState("");
  const [participantError, setParticipantError] = useState("");
  const [nameError, setNameError] = useState("");

  const formatTimeValue = (value: Dayjs | null) => {
    if (!value) {
      return "";
    }

    return new Intl.DateTimeFormat(
      useAmPm ? "en-US" : i18n.language || undefined,
      {
        hour: "2-digit",
        minute: "2-digit",
        hour12: useAmPm,
      },
    ).format(value.toDate());
  };

  const buildStartTimeString = () => formatTimeValue(startTime);

  const buildEndTimeString = () => formatTimeValue(endTime);

  const addParticipant = () => {
    const trimmed = participantInput.trim();
    if (!trimmed) return;
    if (invitees.some((p) => p.toLowerCase() === trimmed.toLowerCase())) {
      setParticipantError(
        t("meetingNotes.createMeeting.errors.duplicateEmail"),
      );
      setTimeout(() => setParticipantError(""), 2500);
      return;
    }
    setInvitees((prev) => [...prev, trimmed]);
    setParticipantInput("");
    setParticipantError("");
  };

  const handleSubmit = () => {
    if (!meetingName.trim()) {
      setNameError(t("meetingNotes.createMeeting.errors.meetingNameRequired"));
      return;
    }
    setNameError("");
    if (participantInput.trim()) {
      addParticipant();
      return;
    }

    // Create meeting object
    const newMeeting: Meeting = {
      name: meetingName.trim(),
      date: date ? date.toDate() : new Date(),
      startTime: buildStartTimeString(),
      endTime: buildEndTimeString(),
      participants: invitees,
      status: MeetingStatus.Planned,
    };

    // Convert to SelectedMeeting format for workflow
    const selectedMeeting: SelectedMeeting = {
      id: nextId,
      title: meetingName.trim(),
      date: date ? date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
      startTime: buildStartTimeString(),
      endTime: buildEndTimeString(),
      attendees: invitees.length,
      originalMeeting: newMeeting,
    };
    onCreateMeeting(selectedMeeting);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "#242C63",
        borderWidth: 2,
        borderRadius: "12px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#242C63", fontSize: "0.9375rem" }}
          >
            {t("meetingNotes.createMeeting.title")}
          </Typography>
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{ color: "#64748B", "&:hover": { backgroundColor: "#F1F5F9" } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Meeting Name */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.createMeeting.fields.meetingName")}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t(
                "meetingNotes.createMeeting.placeholders.meetingName",
              )}
              value={meetingName}
              onChange={(e) => {
                setMeetingName(e.target.value);
                if (e.target.value.trim()) setNameError("");
              }}
              error={!!nameError}
              helperText={nameError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#FFFFFF",
                  "& fieldset": { borderColor: "#E5E7EB" },
                  "&:hover fieldset": { borderColor: "#242C63" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#242C63",
                  },
                },
              }}
            />
          </Box>

          {/* Date row */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.createMeeting.fields.date")}
            </Typography>
            <DatePicker
              value={date}
              onChange={setDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                },
              }}
            />
          </Box>

          {/* Time picker */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: "#64748B" }} />
              {t("meetingNotes.createMeeting.fields.time")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimePicker
                value={startTime}
                onChange={setStartTime}
                ampm={useAmPm}
                minutesStep={5}
                format={useAmPm ? "hh:mm A" : "HH:mm"}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 140,
                      ...getTimeInputSx(),
                    },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: "#64748B",
                  fontWeight: 500,
                  px: 0.5,
                }}
              >
                –
              </Typography>
              <TimePicker
                value={endTime}
                onChange={setEndTime}
                ampm={useAmPm}
                minutesStep={5}
                format={useAmPm ? "hh:mm A" : "HH:mm"}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      minWidth: 140,
                      ...getTimeInputSx(),
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* Invitees */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748B",
                mb: 0.75,
              }}
            >
              {t("meetingNotes.createMeeting.fields.invitees")}
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              open={false}
              options={[] as string[]}
              value={invitees}
              inputValue={participantInput}
              onInputChange={(_, newValue, reason) => {
                if (reason === "input") {
                  setParticipantInput(newValue);
                  if (participantError) setParticipantError("");
                }
              }}
              onChange={(_, newValue, reason) => {
                if (reason === "createOption") {
                  addParticipant();
                } else if (
                  reason === "removeOption" ||
                  reason === "clear"
                ) {
                  setInvitees(newValue as string[]);
                }
              }}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...chipProps } = getItemProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...chipProps}
                      label={option}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "0.75rem",
                        backgroundColor: "#EAE8E3",
                        color: "#242C63",
                        borderRadius: "4px",
                        "& .MuiChip-deleteIcon": {
                          fontSize: 14,
                          color: "#64748B",
                          "&:hover": { color: "#1A2048" },
                        },
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  error={!!participantError}
                  placeholder={
                    invitees.length === 0
                      ? t(
                          "meetingNotes.editableMeetingInfo.participantPlaceholder",
                        )
                      : ""
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Tab" && participantInput.trim()) {
                      e.preventDefault();
                      addParticipant();
                    }
                  }}
                  onBlur={() => {
                    if (participantInput.trim()) addParticipant();
                  }}
                />
              )}
            />
            {participantError && (
              <Typography
                sx={{ mt: 0.5, fontSize: "0.75rem", color: "#EF4444" }}
              >
                {participantError}
              </Typography>
            )}
            <Typography sx={{ mt: 0.5, fontSize: "0.75rem", color: "#828CA1" }}>
              {t("meetingNotes.createMeeting.helpers.invitees")}
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              pt: 0.5,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={onCancel}
              sx={{
                fontWeight: 500,
                fontSize: "0.8125rem",
                textTransform: "none",
                borderColor: "#E5E7EB",
                color: "#64748B",
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#242C63",
                  color: "#242C63",
                  backgroundColor: "#F8FAFC",
                  boxShadow: "none",
                },
              }}
            >
              {t("button.cancel")}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              sx={{
                fontWeight: 500,
                fontSize: "0.8125rem",
                textTransform: "none",
                backgroundColor: "#242C63",
                color: "#FFFFFF",
                px: 2,
                boxShadow: "none !important",
                "&:hover": { backgroundColor: "#353D7A", boxShadow: "none" },
                "&:active": { backgroundColor: "#1A2048" },
              }}
            >
              {t("meetingNotes.createMeeting.actions.createMeeting")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CreateMeetingCard;
